import React, { useLayoutEffect, useRef, useState } from "react";
import { useIsMobile } from "../hooks/use-is-mobile";

interface ScaledPreviewProps {
  /** Width the document is laid out at before scaling (roughly an A4 page on screen). */
  documentWidth?: number;
  children: React.ReactNode;
}

/**
 * Lays printable documents out at desktop width and scales them down to fit a phone,
 * so the preview looks like the printed page instead of reflowing.
 */
const ScaledPreview: React.FC<ScaledPreviewProps> = ({ documentWidth = 820, children }) => {
  const isMobile = useIsMobile();
  const frameRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);

  useLayoutEffect(() => {
    if (!isMobile) return;
    const frame = frameRef.current;
    const content = contentRef.current;
    if (!frame || !content) return;

    const measure = () => {
      setScale(Math.min(1, frame.clientWidth / documentWidth));
      setContentHeight(content.offsetHeight);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    observer.observe(content);
    return () => observer.disconnect();
  }, [isMobile, documentWidth]);

  if (!isMobile) return <>{children}</>;

  return (
    <div
      ref={frameRef}
      className="relative w-full overflow-hidden"
      style={{ height: contentHeight * scale }}
    >
      {/* Absolutely positioned so the unscaled width never stretches ancestors (e.g. Radix ScrollArea). */}
      <div
        ref={contentRef}
        className="absolute left-0 top-0"
        style={{ width: documentWidth, transform: `scale(${scale})`, transformOrigin: "top left" }}
      >
        {children}
      </div>
    </div>
  );
};

export default ScaledPreview;
